import React, {useContext, useEffect, useState} from "react";

import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {UserContext} from "../../../context/UserContext"
import {useForm} from "common/src/hooks/form/formHook";
import {useNotifications} from "common/src/hooks/notification/notificationHook";
import * as yup from "yup";

import Input from "../../../../../common/src/components/widget/Input";
import PromiseBuilder from "../../../../../common/src/utils/PromiseUtils";
import ApiClient from "../../../../../common/src/api/ApiClient";
import LocationSearch
  from "../../../../../common/src/components/widget/LocationSearch";
import Button from "../../../../../common/src/components/widget/Button";
import FileUpload from "../../../../../common/src/components/widget/FileUpload";
import {StringNotEmptyRegex} from "common/src/regex/StringNotEmptyRegex";
import RichText from "common/src/components/widget/RichText";
import FormContainer from "common/src/components/form/FormContainer";

function StageForm() {
  const {t} = useTranslation();
  const {user} = useContext(UserContext);
  const navigate = useNavigate();
  const {addErrorNotification, addSuccessNotification} = useNotifications();
  const {stageDetailsId} = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const schema = {
    code: yup.string().matches(StringNotEmptyRegex, t('field_format_error')).max(32, t('length_exceeded_error')).required(t('required_field')),
    name: yup.string().matches(StringNotEmptyRegex, t('field_format_error')).max(128, t('length_exceeded_error')).required(t('required_field')),
    url: yup.string().matches(StringNotEmptyRegex, t('field_format_error')).max(1024, t('length_exceeded_error')).required(t('required_field')),
    durationHours: yup.number().min(0, t('greater_than_zero')).required(t('required_field')).typeError(t('required_field')),
    distance: yup.number().max(10000000, t('length_exceeded_error')).required(t('required_field')).typeError(t('required_field')),
    startLocation: yup.string().required(t('required_field')),
    endLocation: yup.string().required(t('required_field')),
    germanDescription: yup.string().matches(StringNotEmptyRegex, t('field_format_error')).max(4096, t('length_exceeded_error')),
    englishDescription: yup.string().matches(StringNotEmptyRegex, t('field_format_error')).max(4096, t('length_exceeded_error')),

    image: yup.object().shape({
      name: yup.string().matches(StringNotEmptyRegex, t('field_format_error')).max(128, t('length_exceeded_error')).required(t('required_field')),
      content: yup.string().matches(StringNotEmptyRegex, t('field_format_error')).required(t('required_field')),
    }).required(t('required_field')),
  };

  const {
    values,
    setFormNotDirty,
    getField,
    handleSubmit,
    setValue,
    setValues,
    isEdited
  } = useForm(submitStage, {}, schema);

  useEffect(() => {

    if (!stageDetailsId) {
      return;
    }
    new PromiseBuilder()
      .then(async () => ApiClient.get(`/api/v1/booking/stage/${stageDetailsId}`))
      .then(async (res) => {
        let data = res.data.payload;
        setValues(data);
        setValue("image", data.image, undefined, false)
        setValue("durationHours", data.duration / 60, undefined, false)
      })
      .catch((res) => {
        return addErrorNotification(t("stage.toast.fetch_failed_code", {code: res.code}), t("stage.toast.error"));
      })
      .run();
  }, []);


  function createRequestDataObject() {
    return {
      id: values.id,
      code: values.code,
      name: values.name,
      startLocation: values.startLocation,
      endLocation: values.endLocation,
      germanDescription: values.germanDescription,
      englishDescription: values.englishDescription,
      url: values.url,
      duration: values.durationHours * 60,
      distance: values.distance,
      image: {
        name: values.image.name,
        content: values.image.content,
      }
    };
  }

  function editRequestDataObject() {
    return {
      id: values.id,
      code: values.code,
      name: values.name,
      startLocation: values.startLocation,
      endLocation: values.endLocation,
      germanDescription: values.germanDescription,
      englishDescription: values.englishDescription,
      url: values.url,
      duration: values.durationHours * 60,
      distance: values.distance,
      modifiedBy: user.firstName + " " + user.lastName,
      image: {
        name: values.image.name,
        content: values.image.content,
      }
    };
  }

  function submitStage() {

    let request = stageDetailsId ?
      async () => ApiClient.put(`/api/v1/booking/stage/${stageDetailsId}`, requestDataEdit) :
      async () => ApiClient.post(`/api/v1/booking/stage`, requestDataCreate);

    const successMessage = t(`stage.toast.success_${stageDetailsId ? 'edit_stage' : 'create_stage'}`);
    const requestDataCreate = createRequestDataObject();
    const requestDataEdit = editRequestDataObject();
    setIsLoading(true);

    new PromiseBuilder()
      .then((request))
      .then(() => {
        addSuccessNotification(successMessage, t("stage.toast.success"));
        setFormNotDirty();
        setIsLoading(false);
      })
      .then(() => {navigate('/stage')})
      .catch((res) => {
        addErrorNotification(t("stage.toast.submit_failed_status", {status: res.response.statusText}), t("stage.toast.error"))
        setIsLoading(false);
      })
      .run();
  }

  return (
    <div>
      <div className="card">
        <div className={"card-title"}>
          <span
            className="icon fz fz-signs-post large has-padding-right-10 my-5 ml-4 "/>
          <span className="subtitle is-4 ">  {t('stage.stage')}  </span>
        </div>
        <div className="card-content">
          <FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
            <div className="columns">
              <div className="column is-4">
                <Input
                  label={t('stage.code')}
                  formField={getField("code")}
                />
                <LocationSearch label={t('stage.location_from')}
                                formField={getField("startLocation")}
                />
                <Input type="number"
                       numberFormat={{decimalPlaces: 2, negative: false}}
                       formatDecimalNumber={true}
                       label={t('stage.duration')}
                       unit="h"
                       formField={getField("durationHours")}/>
              </div>
              <div className="column is-4">
                <Input
                  label={t('stage.name')}
                  formField={getField("name")}
                />
                <LocationSearch label={t('stage.location_to')}
                                formField={getField("endLocation")}
                />
                <Input
                  label={t('stage.distance')}
                  type="number"
                  numberFormat={{decimalPlaces: 2, negative: false}}
                  formatDecimalNumber={true}
                  unit="km"
                  formField={getField("distance")}
                />
              </div>
              <div className="column is-4">
                <Input
                  label={t('stage.url')}
                  formField={getField("url")}
                />
              </div>
            </div>
            <div className="columns">
              <div className="column is-8">
                <RichText label={t('stage.english_description')}
                          formField={getField("englishDescription")}
                />
                <div className="mt-5">
                  <RichText label={t('stage.german_description')}
                            formField={getField("germanDescription")}
                  />
                </div>
              </div>
              <div className="column is-4">
                <FileUpload formField={getField("image")}
                />
              </div>
            </div>
            <div className="mt-5">
              <Button
                isSubmit={true}
                isLoading={isLoading}
                color="primary"
                label={stageDetailsId ? t('stage.save') : t('stage.create')}
              />
              <Button
                color="primary"
                label={t('stage.cancel')}
                isOutlined={true}
                onClick={() => {
                  navigate('/stage')
                }}
              />
            </div>
          </FormContainer>
        </div>
      </div>
    </div>
  );
}

export default StageForm;
